/**
 * @fileoverview Game Controls Component
 * @description Header with timer, difficulty, and game controls
 */

import React, { memo } from "react";
import { View, Text, Pressable, useColorScheme } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { GameTimer } from "./GameTimer";
import { SudokuDifficulty, DIFFICULTY_CONFIGS } from "../types";

interface GameControlsProps {
  /** Current difficulty */
  difficulty: SudokuDifficulty;
  /** Time in seconds */
  timerSeconds: number;
  /** Whether game is paused */
  isPaused: boolean;
  /** Number of mistakes made */
  mistakes: number;
  /** Maximum mistakes allowed */
  maxMistakes: number;
  /** Number of hints used */
  hintsUsed: number;
  /** Maximum hints allowed */
  maxHints: number;
  /** Called when pause is pressed */
  onPausePress: () => void;
  /** Called when back/exit is pressed */
  onBackPress: () => void;
}

/**
 * Game controls header
 */
export const GameControls = memo<GameControlsProps>(
  ({
    difficulty,
    timerSeconds,
    isPaused,
    mistakes,
    maxMistakes,
    hintsUsed,
    maxHints,
    onPausePress,
    onBackPress,
  }) => {
    const colorScheme = useColorScheme();
    const isDark = colorScheme === "dark";

    const config = DIFFICULTY_CONFIGS[difficulty];

    return (
      <View
        style={{
          width: "100%",
          paddingHorizontal: 16,
          paddingVertical: 12,
        }}
      >
        {/* Top row: Back, Timer, Pause */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          {/* Back button */}
          <Pressable
            onPress={onBackPress}
            accessibilityRole="button"
            accessibilityLabel="Terug"
            style={({ pressed }) => ({
              padding: 8,
              borderRadius: 8,
              backgroundColor: pressed
                ? isDark
                  ? "#374151"
                  : "#E5E7EB"
                : "transparent",
            })}
          >
            <Ionicons
              name="arrow-back"
              size={24}
              color={isDark ? "#F9FAFB" : "#374151"}
            />
          </Pressable>

          {/* Timer */}
          <GameTimer seconds={timerSeconds} isPaused={isPaused} size="large" />

          {/* Pause button */}
          <Pressable
            onPress={onPausePress}
            accessibilityRole="button"
            accessibilityLabel={isPaused ? "Hervat spel" : "Pauzeer spel"}
            style={({ pressed }) => ({
              padding: 8,
              borderRadius: 8,
              backgroundColor: pressed
                ? isDark
                  ? "#374151"
                  : "#E5E7EB"
                : "transparent",
            })}
          >
            <Ionicons
              name={isPaused ? "play" : "pause"}
              size={24}
              color={isDark ? "#F9FAFB" : "#374151"}
            />
          </Pressable>
        </View>

        {/* Bottom row: Difficulty + Stats */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Difficulty badge */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
              paddingHorizontal: 12,
              paddingVertical: 6,
              borderRadius: 16,
              backgroundColor: config.color + "20",
            }}
          >
            <View
              style={{
                width: 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: config.color,
              }}
            />
            <Text
              style={{
                fontSize: 14,
                fontWeight: "600",
                color: config.color,
              }}
            >
              {config.label}
            </Text>
          </View>

          {/* Stats */}
          <View style={{ flexDirection: "row", gap: 16 }}>
            {/* Mistakes */}
            <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
              <Ionicons
                name="close-circle-outline"
                size={18}
                color={
                  mistakes >= maxMistakes - 1
                    ? "#EF4444"
                    : isDark
                    ? "#9CA3AF"
                    : "#6B7280"
                }
              />
              <Text
                style={{
                  fontSize: 14,
                  fontWeight: "500",
                  color:
                    mistakes >= maxMistakes - 1
                      ? "#EF4444"
                      : isDark
                      ? "#F9FAFB"
                      : "#374151",
                }}
              >
                {mistakes}/{maxMistakes}
              </Text>
            </View>

            {/* Hints */}
            <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
              <Ionicons
                name="bulb-outline"
                size={18}
                color={isDark ? "#9CA3AF" : "#6B7280"}
              />
              <Text
                style={{
                  fontSize: 14,
                  fontWeight: "500",
                  color: isDark ? "#F9FAFB" : "#374151",
                }}
              >
                {maxHints - hintsUsed}
              </Text>
            </View>
          </View>
        </View>
      </View>
    );
  }
);

GameControls.displayName = "GameControls";

